import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { MiniTable } from '@/components/tables/MiniTable/MiniTable';
import { useFederalEstateTaxFormatted } from '@/modules/irs/useIRSConstants';
import { COLORS } from '@/styles/tokens/colors';
import {
  formatCurrencyNoDecimals,
  formatCurrencyNoDecimalsAccounting,
} from '@/utils/formatting/currency';

import {
  TaxDetail_FederalTaxDetailFragment,
  TaxDetail_GiftTaxDetailFragment,
} from '../../graphql/TaxDetail.generated';
import { getFederalTaxFields } from '../../taxes.utils';

interface FederalTaxMiniTableProps {
  federalTax:
    | TaxDetail_FederalTaxDetailFragment
    | TaxDetail_GiftTaxDetailFragment;
}

export function FederalTaxMiniTable({ federalTax }: FederalTaxMiniTableProps) {
  const rate = useFederalEstateTaxFormatted();

  const displayRate = `${rate || 40}%`;

  const {
    taxableEstate,
    nonTaxableTransfers,
    stateTaxDeduction,
    gstTaxDeduction,
    lifetimeExemptionUsed,
    amountSubjectToTax,
    federalTaxDue,
    valuationDiscountApplied,
  } = getFederalTaxFields(federalTax);

  return (
    <MiniTable
      variant="tally"
      rows={compact([
        {
          label: 'Federal estate tax',
          labelProps: { variant: 'title-xs' },
          value: '',
        },
        {
          label: 'Gross estate',
          value: formatCurrencyNoDecimals(taxableEstate),
        },
        {
          label: 'Charitable and marital bequests',
          value: formatCurrencyNoDecimalsAccounting(
            nonTaxableTransfers.abs().negated()
          ),
        },
        {
          label: 'Valuation discount',
          value: valuationDiscountApplied.greaterThan(0)
            ? `(${formatCurrencyNoDecimals(valuationDiscountApplied)})`
            : formatCurrencyNoDecimals(new Decimal(0)),
        },
        {
          label: 'State tax deduction',
          value: formatCurrencyNoDecimalsAccounting(
            stateTaxDeduction.abs().negated()
          ),
        },
        {
          label: 'GST tax deduction',
          value: formatCurrencyNoDecimalsAccounting(
            gstTaxDeduction.abs().negated()
          ),
        },
        {
          label: 'Lifetime exemption used',
          value: formatCurrencyNoDecimalsAccounting(
            lifetimeExemptionUsed.abs().negated()
          ),
        },
        {
          label: `Amount subject to tax (${displayRate})`,
          value: formatCurrencyNoDecimals(amountSubjectToTax),
        },
        {
          label: 'Federal tax due',
          value: formatCurrencyNoDecimalsAccounting(
            federalTaxDue.abs().negated()
          ),
          valueProps: { color: COLORS.MATH.NEGATIVE },
          labelTooltip: `Assumes taxable estate assets in excess of the remaining federal lifetime exemption are taxed at a ${displayRate} federal estate tax rate.`,
        },
      ])}
    />
  );
}
